/**
 * Email-list helpers shared by the `/settings` Phase 2 tag-style input.
 *
 * The on-disk format for `notify_email` stays comma-separated — the
 * orchestrator's existing `agent-config-resolver.ts` parser keeps
 * working unchanged. This file is the SPA's narrow projection of that
 * format into UI tokens (one chip per email) and the validation /
 * dedupe logic the tag input applies on every keystroke.
 *
 * Validation is deliberately RFC-shaped (a-tag@b-tag.tld), not a full
 * RFC 5322 parser — Resend's send-time bounce is the loud-fail path,
 * and a stricter regex in the UI buys nothing.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split a comma-separated string into trimmed non-empty tokens.
 * Whitespace inside a token is rejected by `isValidEmail`, but
 * leading/trailing whitespace is forgiving — operators paste lists.
 */
export function splitEmailString(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Lightweight RFC-shaped check. Matches the orchestrator's behaviour:
 * the Sheet stores whatever the operator wrote; the platform validates
 * at send time, not at edit time.
 */
export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

/**
 * De-duplicate a list while preserving first-occurrence order. Used by
 * the tag input so pasting `a@x.com, a@x.com, b@x.com` collapses to
 * two chips.
 */
export function dedupeEmails(emails: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of emails) {
    if (seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
}

/**
 * Parse the on-disk format (`"a@x.com,b@y.com"`) into a chip array
 * suitable for the tag input. `null` ⇒ empty list (the cell was
 * missing entirely or held only whitespace).
 */
export function parseEmailList(stored: string | number | null): string[] {
  if (typeof stored !== "string") return [];
  return dedupeEmails(splitEmailString(stored));
}

/**
 * Render a chip array back to the on-disk comma-separated form. The
 * format mirrors what the existing curator/validator/cost-alert
 * resolvers expect. Empty chip array ⇒ empty string (the operator
 * has cleared every recipient — a deliberate choice, not a bug).
 */
export function serializeEmailList(emails: readonly string[]): string {
  return emails.join(",");
}
